import { useMemo, useState } from "react";
import ToolAccessDialog from "./ToolAccessDialog";
import ToolGroup from "./ToolGroup";
import type { MCPConnectionTool } from "../../../services/agent-tools-service";
import { useTranslation } from "react-i18next";

type Props = {
  connectionId: string;
  tools: MCPConnectionTool[];
  isLoading?: boolean;
  errorMessage?: string | null;
};

const PermissionsSection = ({
  connectionId,
  tools,
  isLoading = false,
  errorMessage = null,
}: Props) => {
  const { t } = useTranslation();
  const [accessTool, setAccessTool] = useState<MCPConnectionTool | null>(null);

  // Servers report a tool's type freely, so bucket the ones we know how to
  // label and keep the rest — "other", and anything unrecognized or missing —
  // in their own group rather than implying they write or delete.
  const { readTools, writeTools, otherTools } = useMemo(() => {
    const read: MCPConnectionTool[] = [];
    const write: MCPConnectionTool[] = [];
    const other: MCPConnectionTool[] = [];
    for (const tool of tools) {
      if (tool.type === "read") read.push(tool);
      else if (tool.type === "write" || tool.type === "delete") write.push(tool);
      else other.push(tool);
    }
    return { readTools: read, writeTools: write, otherTools: other };
  }, [tools]);

  return (
    <section className="px-4 py-2">
      <h3 className="mb-1 text-base font-medium text-foreground">
        {t("mcpPage.toolPermissions.title")}
      </h3>

      {isLoading && tools.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">
          {t("mcpPage.toolPermissions.loading")}
        </p>
      ) : errorMessage ? (
        <p className="py-3 text-sm text-destructive">{errorMessage}</p>
      ) : tools.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">
          {t("mcpPage.toolPermissions.empty")}
        </p>
      ) : (
        <>
          {readTools.length > 0 && (
            <ToolGroup
              title={t("mcpPage.toolPermissions.readOnly")}
              tools={readTools}
              onToolChangeAccess={setAccessTool}
              defaultOpen
            />
          )}
          {writeTools.length > 0 && (
            <ToolGroup
              title={t("mcpPage.toolPermissions.writeDelete")}
              tools={writeTools}
              onToolChangeAccess={setAccessTool}
            />
          )}
          {otherTools.length > 0 && (
            <ToolGroup
              title={t("mcpPage.toolPermissions.other")}
              tools={otherTools}
              onToolChangeAccess={setAccessTool}
            />
          )}
        </>
      )}

      <ToolAccessDialog
        open={accessTool !== null}
        onOpenChange={(open) => {
          if (!open) setAccessTool(null);
        }}
        connectionId={connectionId}
        tool={accessTool}
      />
    </section>
  );
};

export default PermissionsSection;
